import React, { useCallback } from "react";
import { commitMutation, graphql } from "react-relay";

import { faLink } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faSpinnerThird } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { fetchQuery } from "relay-runtime";

import { useDebouncedValue } from "~/lib/hooks";
import { addHttpsToNonUrls, isValidUrl } from "~/lib/utils";
import { environment } from "~/models";
import { Button, Tooltip, useModals } from "~/ui";
import { IconInput, Input, Modal } from "~/ui/legacyFellowIndex";
import toast from "~/ui/toast";

import type { Bookmark } from "./Bookmarks";

import type { BookmarkModalAddMutation } from "~/__generated__/BookmarkModalAddMutation.graphql";
import type { BookmarkModalEditMutation } from "~/__generated__/BookmarkModalEditMutation.graphql";
import type {
	BookmarkModalGetLinkQuery,
	BookmarkModalGetLinkQuery$data,
} from "~/__generated__/BookmarkModalGetLinkQuery.graphql";

import styles from "./BookmarkModal.module.css";

export const useBookmarkModal = (streamId: string, bookmark?: Bookmark) => {
	const modals = useModals();
	return useCallback(
		() => modals.open(({ close }) => <BookmarkModal streamId={streamId} onClose={close} bookmarkToEdit={bookmark} />),
		[modals, streamId, bookmark],
	);
};

const checkLinkQuery = graphql`
	query BookmarkModalGetLinkQuery($link: String!) {
		getLinkInfo(url: $link) {
			title
			icon
		}
	}
`;

const addBookmarkMutation = graphql`
	mutation BookmarkModalAddMutation($streamId: ID!, $title: String!, $link: String!) {
		StreamTab_AddBookmarkTab(streamId: $streamId, title: $title, link: $link, isEmbedded: false) {
			stream {
				id
				...Bookmarks_stream @relay(mask: false)
			}
		}
	}
`;

const editBookmarkMutation = graphql`
	mutation BookmarkModalEditMutation($streamId: ID!, $tabId: String!, $title: String, $link: String) {
		StreamTab_EditBookmarkTab(streamId: $streamId, tabId: $tabId, title: $title, link: $link, isEmbedded: false) {
			stream {
				id
				...Bookmarks_stream @relay(mask: false)
			}
		}
	}
`;

// Delay in ms
const DEBOUNCE_DELAY = 500;

type LinkInfo = BookmarkModalGetLinkQuery$data["getLinkInfo"];

type Props = {
	onClose: () => void;
	streamId: string;
	bookmarkToEdit?: Bookmark;
};

function BookmarkModal({ onClose, streamId, bookmarkToEdit }: Props) {
	const [input, setInput] = React.useState(bookmarkToEdit?.url ?? "");
	const debouncedInput = useDebouncedValue(input, DEBOUNCE_DELAY);

	const [isLoading, setIsLoading] = React.useState(false);
	const [linkInfo, setLinkInfo] = React.useState<LinkInfo>(
		bookmarkToEdit
			? {
					title: bookmarkToEdit.title,
					icon: bookmarkToEdit.icon,
				}
			: null,
	);

	const [linkError, setLinkError] = React.useState<string | null>(null);
	const [linkTitle, setLinkTitle] = React.useState(bookmarkToEdit?.title ?? "");
	const [lastCheckedLink, setLastCheckedLink] = React.useState("");
	const [isMutationLoading, setIsMutationLoading] = React.useState(false);

	const onInputChange = React.useCallback((e: React.FormEvent<HTMLInputElement>) => {
		setInput(e.currentTarget.value);
		setLinkError(null);
	}, []);

	const resetForm = () => {
		setLinkInfo(null);
		setLinkTitle("");
	};

	const resetIconInLinkInfo = React.useCallback(() => {
		setLinkInfo(val =>
			val
				? {
						...val,
						icon: null,
					}
				: null,
		);
	}, []);

	React.useEffect(() => {
		if (!debouncedInput) {
			resetForm();
			return;
		}

		if (isValidUrl(addHttpsToNonUrls(debouncedInput))) {
			if (lastCheckedLink == debouncedInput && linkInfo !== null) {
				return;
			}

			setLastCheckedLink(debouncedInput);
			setIsLoading(true);

			fetchQuery<BookmarkModalGetLinkQuery>(environment, checkLinkQuery, { link: debouncedInput })
				.toPromise()
				.then(result => {
					if (!result) {
						return;
					}

					if (result.getLinkInfo == null) {
						setLinkError("Nothing was found while fetching that URL.");
						resetForm();
					} else {
						setLinkError(null);
						setLinkInfo(result.getLinkInfo);

						// This check stops the title from updating on load when there is one to begin with
						if (bookmarkToEdit?.title !== linkTitle && linkTitle !== result.getLinkInfo?.title) {
							setLinkTitle(result.getLinkInfo?.title || "");
						}
					}

					setIsLoading(false);
				});
		} else {
			setLinkError("Couldn't save bookmark. Please check if the link is a valid URL.");
			resetForm();
			setIsLoading(false);
		}
	}, [bookmarkToEdit?.title, debouncedInput, lastCheckedLink, linkInfo, linkTitle]);

	const onCancel = React.useCallback(() => {
		onClose();
	}, [onClose]);

	const onSubmit = React.useCallback(async () => {
		setIsMutationLoading(true);

		if (bookmarkToEdit) {
			commitMutation<BookmarkModalEditMutation>(environment, {
				mutation: editBookmarkMutation,
				variables: {
					streamId: streamId,
					tabId: bookmarkToEdit.id,
					title: linkTitle,
					link: debouncedInput,
				},
				onCompleted: (_, errors) => {
					if (errors) {
						setIsMutationLoading(false);
						toast.error("We had a problem editing that bookmark, please try again.");
						throw new Error((errors || []).map(e => e.message).join(";"));
					}
					toast.success("Bookmark edited successfully!");
					onClose();
				},
				onError: () => {
					setIsMutationLoading(false);
				},
			});
		} else {
			commitMutation<BookmarkModalAddMutation>(environment, {
				mutation: addBookmarkMutation,
				variables: {
					streamId: streamId,
					title: linkTitle,
					link: debouncedInput,
				},
				onCompleted: (_, errors) => {
					if (errors) {
						setIsMutationLoading(false);
						toast.error("We had a problem creating a bookmark, please try again.");
						throw new Error((errors || []).map(e => e.message).join(";"));
					}
					toast.success("Bookmark created successfully!");
					onClose();
				},
				onError: () => {
					setIsMutationLoading(false);
				},
			});
		}
	}, [bookmarkToEdit, debouncedInput, linkTitle, onClose, streamId]);

	const nextButtonDisabled = React.useMemo(
		() => !Boolean(linkInfo) || Boolean(linkError) || isMutationLoading,
		[linkError, linkInfo, isMutationLoading],
	);

	const tooltipText = React.useMemo(() => {
		if (isMutationLoading) {
			return "Loading...";
		} else if (!Boolean(linkInfo)) {
			return "Paste a link to continue";
		} else if (Boolean(linkError)) {
			return "Nothing was found while fetching that URL.";
		} else {
			return "";
		}
	}, [isMutationLoading, linkError, linkInfo]);

	return (
		<Modal onClose={onCancel}>
			<Modal.Header onClose={onCancel}>
				<Modal.Title>{bookmarkToEdit ? "Edit bookmark" : "Add a bookmark to this series"}</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<div className={styles.container}>
					<div className={styles.inputWrapper}>
						<label className={styles.label}>Link</label>

						<div className={styles.titleWrapper}>
							<Input
								className={cx(styles.titleInput, { [styles.errorInput]: linkError })}
								type="text"
								placeholder="https://website.acme.com"
								value={input}
								onChange={onInputChange}
								autoFocus
								name="bookmark_link"
							/>
							{isLoading && (
								<FontAwesomeIcon className={styles.spinner} icon={faSpinnerThird} spin color="var(--vividRoyal)" />
							)}
						</div>
					</div>

					{linkError && <div className={styles.errorMessage}>{linkError}</div>}

					{linkInfo && (
						<div className={styles.inputWrapper}>
							<label className={styles.label}>Name</label>
							<IconInput
								type="text"
								value={linkTitle}
								onValueChange={setLinkTitle}
								className={styles.nameInput}
								icon={
									linkInfo.icon ? (
										<img src={linkInfo.icon} alt="icon" onError={resetIconInLinkInfo} />
									) : (
										<FontAwesomeIcon fixedWidth icon={faLink} />
									)
								}
								name="bookmark_name"
							/>
						</div>
					)}
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" variant="secondary" onClick={onCancel}>
					Cancel
				</Button>
				<Tooltip disabled={!nextButtonDisabled || linkError !== null} content={tooltipText}>
					{/* We need to wrap the button in a div since we only want to see the tooltip when the button
					is disabled. A disabled button isn't a valid hover target thus the need for a wrapping div */}
					<div>
						<Button size="normal" variant="primary" disabled={nextButtonDisabled} onClick={onSubmit}>
							{bookmarkToEdit ? "Save" : "Add"}
						</Button>
					</div>
				</Tooltip>
			</Modal.Footer>
		</Modal>
	);
}
